import React, { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import PaystackPop from "@paystack/inline-js";
import { useNavigate } from "react-router-dom";
import {
    ArrowRight,
    CheckCircle2,
    Clock3,
    Lock,
    Maximize2,
    Play,
    RotateCcw,
    ShieldCheck,
    Sparkles,
    XCircle,
} from "lucide-react";
import { toast } from "sonner";
import {
    createJBATxRef,
    fulfillJBAPayment,
    getJBAPaystackAmount,
    getOrCreateJBASessionId,
    JBALead,
    jbaConfig,
    loadJBALead,
    saveJBACheckoutRecord,
    saveJBALead,
    submitJBAFreePdfLead,
} from "../../utils/jba";
import { config } from "../../utils/config";
import useVideoAnalytics from "../../hooks/useVideoAnalytics";

const STORAGE_TIME_KEY = "jba-video-time";
const STORAGE_COMPLETE_KEY = "jba-video-complete";
const FREE_PDF_PROMPT_TIME_SECONDS = 50;
const FREE_PDF_CLOSE_DELAY_SECONDS = 5;
const FORM_UNLOCK_TIME_SECONDS = 11 * 60;
const TOTAL_RECENT_BUYER_SLOTS = 100;
const RECENT_BUYER_INITIAL_SLOTS_MIN = 71;
const RECENT_BUYER_INITIAL_SLOTS_MAX = 87;
const RECENT_BUYER_SLOTS_LEFT_STORAGE_KEY = "jba-recent-buyer-slots-left-v4";
const MIN_RECENT_BUYER_SLOTS_LEFT = 89;
const OFFER_COUNTDOWN_STORAGE_KEY = "jba-offer-expiry";
const OFFER_COUNTDOWN_DURATION_MS = 24 * 60 * 60 * 1000;

type FormErrors = Partial<Record<keyof JBALead, string>>;
type ProgressCurveStop = {
    actual: number;
    display: number;
};
type FreePdfLead = {
    fullName: string;
    email: string;
    phone: string;
};
type FreePdfFormErrors = Partial<Record<keyof FreePdfLead, string>>;
type CountdownParts = {
    hours: string;
    minutes: string;
    seconds: string;
};

const emptyLead: JBALead = {
    name: "",
    email: "",
    phone: "",
};
const emptyFreePdfLead: FreePdfLead = {
    fullName: "",
    email: "",
    phone: "",
};

const DISPLAY_PROGRESS_STOPS: ProgressCurveStop[] = [
    { actual: 0, display: 0 },
    { actual: 6, display: 18 },
    { actual: 22, display: 40 },
    { actual: 100, display: 100 },
];
const RECENT_BUYER_NAMES = [
    "Chinedu",
    "Ngozi",
    "Obinna",
    "Ifeoma",
    "Nnamdi",
    "Sadiq",
    "Amina",
    "Musa",
    "Zainab",
    "Ibrahim",
    "Uduak",
    "Itoro",
    "Aniefiok",
    "Eno",
    "Idara",
    "Bello",
    "Aisha",
    "Usman",
    "Fatima",
    "Abdullahi",
    "Osaze",
    "Efe",
    "Osagie",
    "Igho",
    "Oghenetega",
    "James",
    "John",
    "Michael",
    "David",
    "Daniel",
    "Christopher",
    "Matthew",
    "Andrew",
    "Joseph",
    "Samuel",
    "Destiny",
    "Christianah",
    "Mary",
    "Jessica",
    "Somto",
    "Precious",
    "Amanda",
    "Elizabeth",
    "Hannah",
    "Stephen",
    "Grace",
];
const RECENT_BUYER_VISIBLE_MS = 3200;
const GURU_MYTHS = [
    {
        myth: "You need a physical shop to sell.",
        correction:
            "People are doing millions per week without one. Your location is far less important than your offer and your ability to collect payments.",
    },
    {
        myth: "You need a perfect brand, logo, and website first.",
        correction:
            "Day one is about first sales, not a polished brand system. Trust signals and speed matter more than visual perfection at the start.",
    },
    {
        myth: "You need a lot of capital to start.",
        correction:
            "₦200,000 is already enough to move. Inside the training, the setup and action plan are broken down so you can start with clarity.",
    },
    {
        myth: "You need to be technical.",
        correction:
            "If you can send a WhatsApp message, you can follow this process. The stack is intentionally simple and phone-friendly.",
    },
];
const STARTING_STEPS = [
    {
        step: "01",
        title: "What to sell and who to sell to",
        description:
            "Choose from products Nigerians are already paying for right now. Fashion, food, skincare, thrift, services, and digital products.",
    },
    {
        step: "02",
        title: "Look like a real business fast",
        description:
            "Clean positioning, trust-building content, and page structure that make buyers feel safe instead of skeptical.",
    },
    {
        step: "03",
        title: "Make your first money now",
        description:
            "Start with one paying customer, then use outreach and ads that convert instead of vanity metrics that do nothing.",
    },
];

const FREE_PDF_BULLETS = [
    "What to post every single day for 30 days",
    "Exact captions that build trust and trigger sales",
    "Works even  if you haven't made your first sale yet",
]
const TESTIMONIAL_CARDS = [
    {
        name: "Chinedu M.",
        tag: "Online seller",
        metric: "₦180k first week",
        quote:
            "Bro, I just made my first ₦180k this week. The outreach script is fire.",
    },
    {
        name: "Tolu Badmus",
        tag: "Lagos · Thrift",
        metric: "₦420k / month",
        quote:
            "The product PDF alone was worth more than the price. I picked one offer, set up by night, and woke up to my first order.",
    },
    {
        name: "Fatima Musa",
        tag: "Abuja · Skincare",
        metric: "₦1.2M / month",
        quote:
            "I stopped overthinking, followed the process, and customers started paying. That changed everything.",
    },
    {
        name: "Kemi J.",
        tag: "Online",
        metric: "3 sales before lunch",
        quote:
            "I had been 'about to start' for two years. Then I used the system and finally saw real sales.",
    },
];
const PACKAGE_ITEMS = [
    {
        name: 'The "What to Sell" PDF',
        description:
            "25 products and categories with proven demand in Nigeria right now.",
        value: "₦30,000",
    },
    {
        name: "Jellosite online store access",
        description:
            "Launch from your phone with Paystack checkout, automatic orders, and zero technical setup stress.",
        value: "₦400,000",
    },
    {
        name: "Instagram ads mastery course",
        description:
            "A complete ads course built around converting sales, not empty likes.",
        value: "₦70,000",
    },
    {
        name: "1-on-1 onboarding call with Davies",
        description:
            "Direct guidance so your setup and positioning are corrected early.",
        value: "₦50,000",
    },
    {
        name: "Private community of Nigerian sellers",
        description:
            "Peer support, weekly direction, and a room full of people taking action.",
        value: "₦25,000",
    },
    {
        name: "Trust-building content bundle",
        description:
            "Plug-and-play templates that make your business look credible fast.",
        value: "₦15,000",
    },
];
const FAQ_ITEMS = [
    {
        question: "Will this work for the kind of business I want to sell?",
        answer:
            "Yes. The system works for physical products, digital products, and services as long as Nigerians are already buying what you want to offer.",
    },
    {
        question: "What if I do not know what to sell yet?",
        answer:
            "That is already covered. You get a curated list of products and categories with existing demand so you can choose before risking money.",
    },
    {
        question: "What if I have never built a website before?",
        answer:
            "You do not need to be technical. The setup is designed to be done from your phone, and the onboarding removes the heavy lifting.",
    },
    {
        question: "What if I am not satisfied?",
        answer:
            "There is a 30-day founder guarantee backed by a real call and practical direction, not a vague support response.",
    },
];

const splitLeadName = (name: string) => {
    const [firstName = "", ...rest] = name.trim().split(/\s+/);

    return {
        firstName,
        lastName: rest.join(" "),
    };
};

const normalizePaystackChannel = (channel: string) => {
    if (channel === "banktransfer") {
        return "bank_transfer";
    }

    return channel;
};

const normalizePaymentStatus = (status: string | undefined) => {
    const normalizedStatus = status?.trim().toLowerCase();

    if (
        normalizedStatus &&
        ["success", "successful", "approved", "completed"].includes(
            normalizedStatus
        )
    ) {
        return "successful";
    }

    return status || "successful";
};

const validateLeadForm = (lead: JBALead) => {
    const nextErrors: FormErrors = {};

    if (!lead.name.trim()) {
        nextErrors.name = "Enter your name.";
    }

    if (!lead.email.trim()) {
        nextErrors.email = "Enter your email address.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lead.email.trim())) {
        nextErrors.email = "Enter a valid email address.";
    }

    if (!lead.phone.trim()) {
        nextErrors.phone = "Enter your phone number.";
    } else if (!/^[+()\-\s\d]{7,20}$/.test(lead.phone.trim())) {
        nextErrors.phone = "Enter a valid phone number.";
    }

    return nextErrors;
};

const validateFreePdfLead = (lead: FreePdfLead) => {
    const nextErrors: FreePdfFormErrors = {};

    if (!lead.fullName.trim()) {
        nextErrors.fullName = "Enter your full name.";
    }

    if (!lead.email.trim()) {
        nextErrors.email = "Enter your email address.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lead.email.trim())) {
        nextErrors.email = "Enter a valid email address.";
    }

    if (!lead.phone.trim()) {
        nextErrors.phone = "Enter your phone number.";
    } else if (!/^[+()\-\s\d]{7,20}$/.test(lead.phone.trim())) {
        nextErrors.phone = "Enter a valid phone number.";
    }

    return nextErrors;
};

const getDisplayedWatchPercentage = (actualPercentage: number) => {
    const normalizedActual = Math.min(100, Math.max(0, actualPercentage));

    for (let index = 1; index < DISPLAY_PROGRESS_STOPS.length; index += 1) {
        const previousStop = DISPLAY_PROGRESS_STOPS[index - 1];
        const currentStop = DISPLAY_PROGRESS_STOPS[index];

        if (normalizedActual > currentStop.actual) {
            continue;
        }

        const actualRange = currentStop.actual - previousStop.actual;
        if (actualRange <= 0) {
            return currentStop.display;
        }

        const progressWithinRange =
            (normalizedActual - previousStop.actual) / actualRange;

        return (
            previousStop.display +
            progressWithinRange * (currentStop.display - previousStop.display)
        );
    }

    return normalizedActual;
};

const getFormUnlockTime = (duration: number) =>
    Math.min(duration, FORM_UNLOCK_TIME_SECONDS);

const getRandomRecentBuyerName = (previousName: string | null) => {
    if (RECENT_BUYER_NAMES.length === 0) {
        return "";
    }

    if (RECENT_BUYER_NAMES.length === 1) {
        return RECENT_BUYER_NAMES[0];
    }

    let nextName = previousName;

    while (nextName === previousName) {
        nextName =
            RECENT_BUYER_NAMES[
            Math.floor(Math.random() * RECENT_BUYER_NAMES.length)
            ];
    }

    return nextName;
};

const getRecentBuyerDelayMs = () =>
    (Math.floor(Math.random() * 13) + 3) * 1000;

const getInitialRecentBuyerSlotsLeft = () =>
    Math.floor(
        Math.random() *
        (RECENT_BUYER_INITIAL_SLOTS_MAX - RECENT_BUYER_INITIAL_SLOTS_MIN + 1)
    ) + RECENT_BUYER_INITIAL_SLOTS_MIN;

const loadRecentBuyerSlotsLeft = () => {
    if (typeof window === "undefined") {
        return RECENT_BUYER_INITIAL_SLOTS_MAX;
    }

    try {
        const storedRawValue = localStorage.getItem(
            RECENT_BUYER_SLOTS_LEFT_STORAGE_KEY
        );
        const storedValue = Number(storedRawValue);

        if (
            !storedRawValue ||
            !Number.isFinite(storedValue) ||
            storedValue < MIN_RECENT_BUYER_SLOTS_LEFT
        ) {
            const initialValue = storedRawValue
                ? MIN_RECENT_BUYER_SLOTS_LEFT
                : getInitialRecentBuyerSlotsLeft();
            localStorage.setItem(
                RECENT_BUYER_SLOTS_LEFT_STORAGE_KEY,
                String(initialValue)
            );
            return initialValue;
        }

        return Math.min(
            TOTAL_RECENT_BUYER_SLOTS,
            Math.max(MIN_RECENT_BUYER_SLOTS_LEFT, Math.floor(storedValue))
        );
    } catch {
        return RECENT_BUYER_INITIAL_SLOTS_MAX;
    }
};

const getInitialFreePdfLead = (): FreePdfLead => {
    const savedLead = loadJBALead();

    return {
        fullName: savedLead?.name ?? emptyFreePdfLead.fullName,
        email: savedLead?.email ?? emptyFreePdfLead.email,
        phone: savedLead?.phone ?? emptyFreePdfLead.phone,
    };
};

const getInitialOfferExpiry = () => {
    if (typeof window === "undefined") {
        return Date.now() + OFFER_COUNTDOWN_DURATION_MS;
    }

    try {
        const storedValue = Number(
            localStorage.getItem(OFFER_COUNTDOWN_STORAGE_KEY)
        );

        if (Number.isFinite(storedValue) && storedValue > Date.now()) {
            return storedValue;
        }

        const nextExpiry = Date.now() + OFFER_COUNTDOWN_DURATION_MS;
        localStorage.setItem(OFFER_COUNTDOWN_STORAGE_KEY, String(nextExpiry));
        return nextExpiry;
    } catch {
        return Date.now() + OFFER_COUNTDOWN_DURATION_MS;
    }
};

const getCountdownParts = (targetTimestamp: number): CountdownParts => {
    const remainingSeconds = Math.max(
        0,
        Math.floor((targetTimestamp - Date.now()) / 1000)
    );
    const hours = Math.floor(remainingSeconds / 3600);
    const minutes = Math.floor((remainingSeconds % 3600) / 60);
    const seconds = remainingSeconds % 60;

    return {
        hours: String(hours).padStart(2, "0"),
        minutes: String(minutes).padStart(2, "0"),
        seconds: String(seconds).padStart(2, "0"),
    };
};

const JBA = () => {
    const navigate = useNavigate();
    const [watchPercentage, setWatchPercentage] = useState(0);
    const [isFormUnlocked, setIsFormUnlocked] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [controlsEnabled, setControlsEnabled] = useState(false);
    const [isMuted, setIsMuted] = useState(true);
    const [showPoster, setShowPoster] = useState(true);
    const [isLaunchingPayment, setIsLaunchingPayment] = useState(false);
    const [recentBuyerName, setRecentBuyerName] = useState("");
    const [isRecentBuyerVisible, setIsRecentBuyerVisible] = useState(false);
    const [isFreePdfPromptOpen, setIsFreePdfPromptOpen] = useState(false);
    const [freePdfCloseCountdown, setFreePdfCloseCountdown] = useState(0);
    const [isSubmittingFreePdf, setIsSubmittingFreePdf] = useState(false);
    const [recentBuyerSlotsLeft, setRecentBuyerSlotsLeft] = useState<number>(
        () => loadRecentBuyerSlotsLeft()
    );
    const [offerExpiryTimestamp, setOfferExpiryTimestamp] = useState<number>(
        () => getInitialOfferExpiry()
    );
    const [offerCountdown, setOfferCountdown] = useState<CountdownParts>(() =>
        getCountdownParts(getInitialOfferExpiry())
    );
    const [freePdfLead, setFreePdfLead] = useState<FreePdfLead>(
        () => getInitialFreePdfLead()
    );
    const [freePdfFormErrors, setFreePdfFormErrors] = useState<FreePdfFormErrors>({});
    const [leadForm, setLeadForm] = useState<JBALead>(
        () => loadJBALead() || emptyLead
    );
    const [formErrors, setFormErrors] = useState<FormErrors>({});
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const videoContainerRef = useRef<HTMLDivElement | null>(null);
    const videoSectionRef = useRef<HTMLDivElement | null>(null);
    const formRef = useRef<HTMLDivElement | null>(null);
    const isFormUnlockedRef = useRef(false);
    const controlsEnabledRef = useRef(false);
    const lastAllowedTimeRef = useRef(0);
    const allowSeekRef = useRef(false);
    const stableDurationRef = useRef(0);
    const lastSavedTimeRef = useRef(0);
    const savedTimeRef = useRef<number | null>(null);
    const recentBuyerNameRef = useRef<string | null>(null);
    const recentBuyerShowTimerRef = useRef<number | null>(null);
    const recentBuyerHideTimerRef = useRef<number | null>(null);
    const freePdfPromptShownRef = useRef(false);
    const freePdfPromptPreviousTimeRef = useRef(0);
    const freePdfCloseTimerRef = useRef<number | null>(null);
    const completedRef = useRef(false);
    const pendingScrollRef = useRef(false);
    const lastPercentageRef = useRef(0);
    const checkoutOutcomeHandledRef = useRef(false);
    const firstUnmuteHandledRef = useRef(false);
    
    // Initialize video analytics tracking
    const analytics = useVideoAnalytics({
        videoId: "jba-course",
        checkpointStep: 10,
        enabled: true,
    });

    const displayedWatchPercentage = getDisplayedWatchPercentage(watchPercentage);

    useEffect(() => {
        let savedTime = 0;
        let savedComplete = false;

        try {
            savedTime = Number(localStorage.getItem(STORAGE_TIME_KEY) || 0);
            savedComplete = localStorage.getItem(STORAGE_COMPLETE_KEY) === "true";
        } catch {
            savedTime = 0;
            savedComplete = false;
        }

        if (savedComplete) {
            completedRef.current = true;
            isFormUnlockedRef.current = true;
            controlsEnabledRef.current = true;
            setIsFormUnlocked(true);
            setWatchPercentage(100);
            setControlsEnabled(true);
            setShowPoster(false);
        }

        if (!savedComplete && Number.isFinite(savedTime) && savedTime > 0) {
            savedTimeRef.current = savedTime;
            lastSavedTimeRef.current = savedTime;
            setShowPoster(false);
        }
        window.fbq?.('track', config.FB_PIXEL_JBA, 'ViewContent');
    }, []);

    useEffect(() => {
        isFormUnlockedRef.current = isFormUnlocked;
    }, [isFormUnlocked]);

    useEffect(() => {
        controlsEnabledRef.current = controlsEnabled;
    }, [controlsEnabled]);

    useEffect(() => {
        if (!isLaunchingPayment) {
            return;
        }

        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = "";
        };

        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [isLaunchingPayment]);

    const scrollToForm = useCallback(() => {
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, []);

    const scrollToVideo = useCallback(() => {
        videoSectionRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });
    }, []);

    const unmuteWithFirstRestart = useCallback(() => {
        const video = videoRef.current;
        if (!video) return;

        video.muted = false;
        setIsMuted(false);

        if (firstUnmuteHandledRef.current || video.currentTime > 15) {
            return;
        }

        firstUnmuteHandledRef.current = true;
        analytics.resetSession();
        completedRef.current = false;
        allowSeekRef.current = true;
        video.currentTime = 0;
        freePdfPromptPreviousTimeRef.current = 0;
        lastAllowedTimeRef.current = 0;
        lastPercentageRef.current = 0;
        setWatchPercentage(0);
        video.play().catch(() => { });
    }, [analytics]);

    const handleUnmute = useCallback(() => {
        unmuteWithFirstRestart();
    }, [unmuteWithFirstRestart]);

    const isFullscreenActive = useCallback(() => {
        const doc = document as Document & { webkitFullscreenElement?: Element | null };
        const video = videoRef.current as HTMLVideoElement & {
            webkitDisplayingFullscreen?: boolean;
        };
        return Boolean(
            document.fullscreenElement ||
            doc.webkitFullscreenElement ||
            video?.webkitDisplayingFullscreen
        );
    }, []);

    const exitFullscreen = useCallback(() => {
        const doc = document as Document & {
            webkitExitFullscreen?: () => void;
            webkitFullscreenElement?: Element | null;
        };
        const video = videoRef.current as HTMLVideoElement & {
            webkitExitFullscreen?: () => void;
            webkitDisplayingFullscreen?: boolean;
        };

        if (document.fullscreenElement && document.exitFullscreen) {
            return document.exitFullscreen();
        }

        if (doc.webkitFullscreenElement && doc.webkitExitFullscreen) {
            doc.webkitExitFullscreen();
            return undefined;
        }

        if (video?.webkitDisplayingFullscreen && video.webkitExitFullscreen) {
            video.webkitExitFullscreen();
        }

        return undefined;
    }, []);

    const requestFullscreen = useCallback(() => {
        const container = videoContainerRef.current;
        const video = videoRef.current as HTMLVideoElement & {
            webkitEnterFullscreen?: () => void;
        };
        const webkitContainer = container as HTMLDivElement & {
            webkitRequestFullscreen?: () => void;
        };

        if (!container || !video) return;

        if (container.requestFullscreen) {
            container.requestFullscreen().catch(() => { });
            return;
        }

        if (webkitContainer?.webkitRequestFullscreen) {
            webkitContainer.webkitRequestFullscreen();
            return;
        }

        if (video.webkitEnterFullscreen) {
            video.webkitEnterFullscreen();
        }
    }, []);

    const toggleFullscreen = useCallback(() => {
        if (isFullscreenActive()) {
            const exitResult = exitFullscreen();
            if (exitResult && typeof exitResult.catch === "function") {
                exitResult.catch(() => { });
            }
            return;
        }

        requestFullscreen();
    }, [exitFullscreen, isFullscreenActive, requestFullscreen]);

    const writeStorage = useCallback((key: string, value: string) => {
        try {
            localStorage.setItem(key, value);
        } catch (error) {
            void error;
        }
    }, []);

    useEffect(() => {
        setOfferCountdown(getCountdownParts(offerExpiryTimestamp));

        const countdownTimer = window.setInterval(() => {
            const now = Date.now();

            if (offerExpiryTimestamp <= now) {
                const nextExpiry = now + OFFER_COUNTDOWN_DURATION_MS;
                setOfferExpiryTimestamp(nextExpiry);
                writeStorage(OFFER_COUNTDOWN_STORAGE_KEY, String(nextExpiry));
                setOfferCountdown(getCountdownParts(nextExpiry));
                return;
            }

            setOfferCountdown(getCountdownParts(offerExpiryTimestamp));
        }, 1000);

        return () => {
            window.clearInterval(countdownTimer);
        };
    }, [offerExpiryTimestamp, writeStorage]);

    const persistRecentBuyerSlotsLeft = useCallback(
        (value: number) => {
            writeStorage(
                RECENT_BUYER_SLOTS_LEFT_STORAGE_KEY,
                String(
                    Math.min(
                        TOTAL_RECENT_BUYER_SLOTS,
                        Math.max(MIN_RECENT_BUYER_SLOTS_LEFT, Math.floor(value))
                    )
                )
            );
        },
        [writeStorage]
    );

    const clearRecentBuyerTimers = useCallback(() => {
        if (recentBuyerShowTimerRef.current !== null) {
            window.clearTimeout(recentBuyerShowTimerRef.current);
            recentBuyerShowTimerRef.current = null;
        }

        if (recentBuyerHideTimerRef.current !== null) {
            window.clearTimeout(recentBuyerHideTimerRef.current);
            recentBuyerHideTimerRef.current = null;
        }
    }, []);

    const clearFreePdfCloseTimer = useCallback(() => {
        if (freePdfCloseTimerRef.current !== null) {
            window.clearInterval(freePdfCloseTimerRef.current);
            freePdfCloseTimerRef.current = null;
        }
    }, []);

    const resumeVideoPlayback = useCallback(() => {
        const video = videoRef.current;
        if (!video) return;

        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === "function") {
            playPromise.catch(() => { });
        }
    }, []);

    const openFreePdfPrompt = useCallback(() => {
        if (freePdfPromptShownRef.current) {
            return;
        }

        freePdfPromptShownRef.current = true;

        const video = videoRef.current;
        if (video && !video.paused) {
            video.pause();
        }

        if (isFullscreenActive()) {
            const exitResult = exitFullscreen();
            if (exitResult && typeof exitResult.catch === "function") {
                exitResult.catch(() => { });
            }
        }

        setIsFreePdfPromptOpen(true);
    }, [exitFullscreen, isFullscreenActive]);

    const saveProgress = useCallback(
        (time: number, force = false) => {
            if (!Number.isFinite(time)) return;
            if (!force && Math.abs(time - lastSavedTimeRef.current) < 1) return;

            lastSavedTimeRef.current = time;
            writeStorage(STORAGE_TIME_KEY, String(time));
        },
        [writeStorage]
    );

    const togglePlayback = useCallback(() => {
        const video = videoRef.current;
        if (!video) return;

        if (video.ended) {
            analytics.resetSession();

            allowSeekRef.current = true;
            video.currentTime = 0;
            freePdfPromptPreviousTimeRef.current = 0;
            lastAllowedTimeRef.current = 0;
            lastPercentageRef.current = 0;
            setWatchPercentage(0);
        }

        if (video.paused || video.ended) {
            if (isMuted) {
                unmuteWithFirstRestart();
            }
            video.play().catch(() => { });
            return;
        }

        video.pause();
    }, [isMuted, analytics, unmuteWithFirstRestart]);

    const restartVideo = useCallback(() => {
        const video = videoRef.current;
        if (!video) return;

        analytics.resetSession();
        completedRef.current = false;
        allowSeekRef.current = true;
        video.currentTime = 0;
        freePdfPromptPreviousTimeRef.current = 0;
        lastAllowedTimeRef.current = 0;
        lastPercentageRef.current = 0;
        setWatchPercentage(0);

        video.play().catch(() => { });
    }, [analytics]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const syncStableDuration = () => {
            const duration = video.duration;
            if (!duration || Number.isNaN(duration)) return;
            stableDurationRef.current = Math.max(stableDurationRef.current, duration);
        };

        const handleLoadedMetadata = () => {
            syncStableDuration();

            const duration = stableDurationRef.current;
            if (!duration) return;
            const unlockTime = getFormUnlockTime(duration);
            freePdfPromptPreviousTimeRef.current = video.currentTime;

            const savedTime = savedTimeRef.current;
            if (!completedRef.current && typeof savedTime === "number" && savedTime > 0) {
                const shouldComplete = savedTime >= duration;
                if (shouldComplete) {
                    completedRef.current = true;
                    isFormUnlockedRef.current = true;
                    controlsEnabledRef.current = true;
                    setIsFormUnlocked(true);
                    setWatchPercentage(100);
                    setControlsEnabled(true);
                    writeStorage(STORAGE_COMPLETE_KEY, "true");
                    writeStorage(STORAGE_TIME_KEY, String(duration));
                    lastSavedTimeRef.current = duration;
                    savedTimeRef.current = null;
                    return;
                }

                const clampedTime = Math.min(savedTime, Math.max(duration - 0.25, 0));
                if (clampedTime >= unlockTime) {
                    isFormUnlockedRef.current = true;
                    setIsFormUnlocked(true);
                }
                allowSeekRef.current = true;
                video.currentTime = clampedTime;
                freePdfPromptPreviousTimeRef.current = clampedTime;
                lastAllowedTimeRef.current = clampedTime;
                savedTimeRef.current = null;
                setWatchPercentage((clampedTime / duration) * 100);
                setShowPoster(false);
            }
        };

        const handleDurationChange = () => {
            syncStableDuration();
        };

        const handleTimeUpdate = () => {
            syncStableDuration();
            const duration = stableDurationRef.current;
            if (!duration) return;
            const unlockTime = getFormUnlockTime(duration);

            if (!completedRef.current && !controlsEnabledRef.current) {
                if (video.currentTime + 0.5 < lastAllowedTimeRef.current) {
                    allowSeekRef.current = true;
                    video.currentTime = lastAllowedTimeRef.current;
                    return;
                }
            }

            const percentage = Math.min(100, (video.currentTime / duration) * 100);
            const nextPercentage = completedRef.current
                ? 100
                : Math.max(lastPercentageRef.current, percentage);
            lastPercentageRef.current = nextPercentage;
            setWatchPercentage(nextPercentage);

            if (video.currentTime > lastAllowedTimeRef.current) {
                lastAllowedTimeRef.current = video.currentTime;
            }

            if (
                !freePdfPromptShownRef.current &&
                !isFormUnlockedRef.current &&
                freePdfPromptPreviousTimeRef.current < FREE_PDF_PROMPT_TIME_SECONDS &&
                video.currentTime >= FREE_PDF_PROMPT_TIME_SECONDS
            ) {
                freePdfPromptPreviousTimeRef.current = video.currentTime;
                openFreePdfPrompt();
                return;
            }

            if (video.currentTime >= unlockTime && !isFormUnlockedRef.current) {
                isFormUnlockedRef.current = true;
                setIsFormUnlocked(true);
            }

            freePdfPromptPreviousTimeRef.current = video.currentTime;
            saveProgress(video.currentTime);

            void analytics.trackProgress(percentage);
        };

        const handleSeeking = () => {
            if (completedRef.current || controlsEnabledRef.current) return;
            if (allowSeekRef.current) {
                allowSeekRef.current = false;
                return;
            }
            if (Math.abs(video.currentTime - lastAllowedTimeRef.current) > 0.5) {
                video.currentTime = lastAllowedTimeRef.current;
            }
        };

        const handleSeeked = () => {
            allowSeekRef.current = false;
        };

        const handleEnded = () => {
            setIsPlaying(false);
            freePdfPromptPreviousTimeRef.current = video.duration || stableDurationRef.current;
            isFormUnlockedRef.current = true;
            controlsEnabledRef.current = true;
            setIsFormUnlocked(true);
            setWatchPercentage(100);
            completedRef.current = true;
            setControlsEnabled(true);
            lastPercentageRef.current = 100;

            const duration = stableDurationRef.current || video.duration;
            writeStorage(STORAGE_COMPLETE_KEY, "true");
            writeStorage(STORAGE_TIME_KEY, String(duration));
            lastSavedTimeRef.current = duration;
            
            // Track video completion in analytics
            analytics.markCompleted();

            if (isFullscreenActive()) {
                pendingScrollRef.current = true;
                const exitResult = exitFullscreen();
                if (exitResult && typeof exitResult.catch === "function") {
                    exitResult.catch(() => { });
                }
                setTimeout(() => {
                    if (pendingScrollRef.current && !isFullscreenActive()) {
                        pendingScrollRef.current = false;
                        scrollToForm();
                    }
                }, 500);
                return;
            }

            scrollToForm();
        };

        const handlePlay = () => {
            setIsPlaying(true);
            setShowPoster(false);
        };

        const handlePause = () => {
            setIsPlaying(false);
            saveProgress(video.currentTime, true);
        };

        video.addEventListener("loadedmetadata", handleLoadedMetadata);
        video.addEventListener("durationchange", handleDurationChange);
        video.addEventListener("timeupdate", handleTimeUpdate);
        video.addEventListener("seeking", handleSeeking);
        video.addEventListener("seeked", handleSeeked);
        video.addEventListener("ended", handleEnded);
        video.addEventListener("play", handlePlay);
        video.addEventListener("pause", handlePause);

        if (video.readyState >= 1) {
            handleLoadedMetadata();
        }

        return () => {
            video.removeEventListener("loadedmetadata", handleLoadedMetadata);
            video.removeEventListener("durationchange", handleDurationChange);
            video.removeEventListener("timeupdate", handleTimeUpdate);
            video.removeEventListener("seeking", handleSeeking);
            video.removeEventListener("seeked", handleSeeked);
            video.removeEventListener("ended", handleEnded);
            video.removeEventListener("play", handlePlay);
            video.removeEventListener("pause", handlePause);
        };
    }, [
        exitFullscreen,
        isFullscreenActive,
        openFreePdfPrompt,
        saveProgress,
        scrollToForm,
        writeStorage,
    ]);

    useEffect(() => {
        const handleFullscreenChange = () => {
            if (pendingScrollRef.current && !isFullscreenActive()) {
                pendingScrollRef.current = false;
                scrollToForm();
            }
        };

        document.addEventListener("fullscreenchange", handleFullscreenChange);
        document.addEventListener("webkitfullscreenchange", handleFullscreenChange);

        const video = videoRef.current;
        if (video) {
            video.addEventListener("webkitendfullscreen", handleFullscreenChange);
        }

        return () => {
            document.removeEventListener("fullscreenchange", handleFullscreenChange);
            document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
            if (video) {
                video.removeEventListener("webkitendfullscreen", handleFullscreenChange);
            }
        };
    }, [isFullscreenActive, scrollToForm]);

    useEffect(() => {
        clearFreePdfCloseTimer();

        if (!isFreePdfPromptOpen) {
            setFreePdfCloseCountdown(0);
            return;
        }

        setFreePdfCloseCountdown(FREE_PDF_CLOSE_DELAY_SECONDS);
        freePdfCloseTimerRef.current = window.setInterval(() => {
            setFreePdfCloseCountdown((currentValue) => {
                if (currentValue <= 1) {
                    clearFreePdfCloseTimer();
                    return 0;
                }

                return currentValue - 1;
            });
        }, 1000);

        return () => {
            clearFreePdfCloseTimer();
        };
    }, [clearFreePdfCloseTimer, isFreePdfPromptOpen]);

    useEffect(() => {
        clearRecentBuyerTimers();

        if (!isFormUnlocked) {
            setIsRecentBuyerVisible(false);
            return;
        }

        let cancelled = false;

        const scheduleNextBuyer = () => {
            recentBuyerShowTimerRef.current = window.setTimeout(() => {
                if (cancelled) return;

                const nextBuyerName = getRandomRecentBuyerName(recentBuyerNameRef.current);
                if (!nextBuyerName) {
                    return;
                }

                recentBuyerNameRef.current = nextBuyerName;
                setRecentBuyerName(nextBuyerName);
                setRecentBuyerSlotsLeft((currentValue) => {
                    const nextValue =
                        currentValue <= MIN_RECENT_BUYER_SLOTS_LEFT
                            ? MIN_RECENT_BUYER_SLOTS_LEFT
                            : currentValue - 1;
                    persistRecentBuyerSlotsLeft(nextValue);
                    return nextValue;
                });
                setIsRecentBuyerVisible(true);

                recentBuyerHideTimerRef.current = window.setTimeout(() => {
                    if (cancelled) return;

                    setIsRecentBuyerVisible(false);
                    scheduleNextBuyer();
                }, RECENT_BUYER_VISIBLE_MS);
            }, getRecentBuyerDelayMs());
        };

        scheduleNextBuyer();

        return () => {
            cancelled = true;
            clearRecentBuyerTimers();
        };
    }, [clearRecentBuyerTimers, isFormUnlocked, persistRecentBuyerSlotsLeft]);

    const handleFreePdfFieldChange =
        (field: keyof FreePdfLead) =>
            (event: React.ChangeEvent<HTMLInputElement>) => {
                const value = event.target.value;
                setFreePdfLead((prev) => ({ ...prev, [field]: value }));

                if (freePdfFormErrors[field]) {
                    setFreePdfFormErrors((prev) => ({ ...prev, [field]: undefined }));
                }
            };

    const closeFreePdfPrompt = () => {
        if (freePdfCloseCountdown > 0) {
            return;
        }

        setIsFreePdfPromptOpen(false);
        setFreePdfFormErrors({});
        resumeVideoPlayback();
    };

    const handleFreePdfSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const normalizedLead: FreePdfLead = {
            fullName: freePdfLead.fullName.trim(),
            email: freePdfLead.email.trim(),
            phone: freePdfLead.phone.trim(),
        };

        const nextErrors = validateFreePdfLead(normalizedLead);
        setFreePdfFormErrors(nextErrors);

        if (Object.keys(nextErrors).length > 0) {
            return;
        }

        const normalizedPhone = normalizedLead.phone;

        setFreePdfLead(normalizedLead);
        setLeadForm((prev) => ({
            ...prev,
            name: normalizedLead.fullName,
            email: normalizedLead.email,
            phone: normalizedPhone,
        }));
        saveJBALead({
            name: normalizedLead.fullName,
            email: normalizedLead.email,
            phone: normalizedPhone,
        });

        setIsSubmittingFreePdf(true);

        try {
            const response = await submitJBAFreePdfLead({
                source: "jba-vsl",
                session_id: getOrCreateJBASessionId(),
                customer: {
                    name: normalizedLead.fullName || undefined,
                    email: normalizedLead.email,
                    phone: normalizedPhone || undefined,
                },
            });

            setIsFreePdfPromptOpen(false);
            setFreePdfFormErrors({});
            toast.success(response?.message || "Check your email for the free PDF.");
            resumeVideoPlayback();
        } catch (error) {
            console.error(error);
            toast.error(
                error instanceof Error
                    ? error.message
                    : "Unable to send the free PDF right now."
            );
        } finally {
            setIsSubmittingFreePdf(false);
        }
    };

    const handleLeadFieldChange =
        (field: keyof JBALead) =>
            (event: React.ChangeEvent<HTMLInputElement>) => {
                const value = event.target.value;
                setLeadForm((prev) => ({ ...prev, [field]: value }));
                if (formErrors[field]) {
                    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
                }
            };

    const handleProceed = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const normalizedLead: JBALead = {
            name: leadForm.name.trim(),
            email: leadForm.email.trim(),
            phone: leadForm.phone.trim(),
        };

        const nextErrors = validateLeadForm(normalizedLead);
        setFormErrors(nextErrors);

        if (Object.keys(nextErrors).length > 0) {
            return;
        }

        saveJBALead(normalizedLead);

        if (!jbaConfig.paystackPublicKey) {
            toast.error("Paystack public key is not configured yet.");
            return;
        }

        if (!jbaConfig.amount || jbaConfig.amount <= 0) {
            toast.error("JBA payment amount is not configured yet.");
            return;
        }

        setIsLaunchingPayment(true);
        checkoutOutcomeHandledRef.current = false;

        try {
            const { firstName, lastName } = splitLeadName(normalizedLead.name);
            const paystack = new PaystackPop();
            const txRef = createJBATxRef();

            saveJBACheckoutRecord({
                txRef,
                lead: normalizedLead,
                createdAt: new Date().toISOString(),
            });

            paystack.newTransaction({
                key: jbaConfig.paystackPublicKey,
                email: normalizedLead.email,
                firstName,
                lastName,
                phone: normalizedLead.phone,
                amount: getJBAPaystackAmount(),
                currency: jbaConfig.currency,
                reference: txRef,
                channels: jbaConfig.paymentOptions
                    .split(",")
                    .map((channel: string) => channel.trim())
                    .map(normalizePaystackChannel)
                    .filter(Boolean),
                metadata: {
                    product_code: jbaConfig.productCode,
                    source: "jba-vsl",
                    customer_name: normalizedLead.name,
                    customer_phone: normalizedLead.phone,
                    custom_fields: [
                        {
                            display_name: "Source",
                            variable_name: "source",
                            value: "jba-vsl",
                        },
                    ],
                },
                onSuccess: async (payment) => {
                    checkoutOutcomeHandledRef.current = true;
                    const paymentTransactionId = String(
                        payment.id ||
                        payment.transaction_id ||
                        payment.transactionId ||
                        payment.trans ||
                        ""
                    );
                    const paymentStatus = normalizePaymentStatus(payment.status);
                    const paymentReference = payment.reference || payment.txref || txRef;

                    window.fbq?.('track', 'Purchase', {value: 59000.00, currency: 'NGN'});
                    const query = new URLSearchParams({
                        status: paymentStatus,
                        transaction_id: paymentTransactionId,
                        tx_ref: paymentReference,
                    });

                    if (paymentReference) {
                        query.set("reference", paymentReference);
                    }

                    if (payment.txref) {
                        query.set("txref", payment.txref);
                    }

                    if (payment.trans) {
                        query.set("trans", String(payment.trans));
                    }

                    try {
                        await fulfillJBAPayment({
                            transaction_id: paymentTransactionId,
                            tx_ref: paymentReference,
                            product_code: jbaConfig.productCode,
                            expected_amount: jbaConfig.amount,
                            expected_currency: jbaConfig.currency,
                            customer: normalizedLead,
                        });
                    } catch (error) {
                        console.error(error);
                        query.set("delivery", "attention");
                    } finally {
                        setIsLaunchingPayment(false);
                        navigate(`/jba/payment/success?${query.toString()}`);
                    }
                },
                onCancel: () => {
                    setIsLaunchingPayment(false);

                    if (checkoutOutcomeHandledRef.current) {
                        return;
                    }

                    checkoutOutcomeHandledRef.current = true;

                    const query = new URLSearchParams({
                        status: "cancelled",
                    });

                    navigate(`/jba/payment/success?${query.toString()}`);
                },
                onError: (error) => {
                    checkoutOutcomeHandledRef.current = true;
                    setIsLaunchingPayment(false);
                    toast.error(error.message || "Unable to start Paystack checkout.");
                },
            });
        } catch (error) {
            console.error(error);
            toast.error(
                error instanceof Error
                    ? error.message
                    : "Unable to start Paystack checkout."
            );
            setIsLaunchingPayment(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#040406] text-white">
            <div className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_top,_rgba(124,58,237,0.18),_transparent_34%),radial-gradient(circle_at_80%_20%,_rgba(245,158,11,0.16),_transparent_24%),linear-gradient(180deg,#050507_0%,#09090f_45%,#040406_100%)]" />

            <div className="border-b border-white/10 bg-gradient-to-r from-[#52131a] via-[#8f1d1d] to-[#381018]">
                <div className="mx-auto flex max-w-6xl flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-col items-center gap-2 sm:flex-row sm:items-center">
                        <span className="text-[11px] font-semibold uppercase tracking-[0.28em] text-white/78">
                            Free video expires in
                        </span>
                        <div className="flex items-center gap-2">
                            {[
                                { label: "HRS", value: offerCountdown.hours },
                                { label: "MIN", value: offerCountdown.minutes },
                                { label: "SEC", value: offerCountdown.seconds },
                            ].map((item) => (
                                <div
                                    key={item.label}
                                    className="min-w-[62px] rounded-2xl border border-white/10 bg-black/25 px-3 py-2 text-center shadow-[0_12px_28px_-18px_rgba(0,0,0,0.9)] backdrop-blur"
                                >
                                    <div className="text-lg font-black leading-none text-white">
                                        {item.value}
                                    </div>
                                    <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-white/60">
                                        {item.label}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="inline-flex items-center justify-center rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-white">
                        Only {recentBuyerSlotsLeft} / {TOTAL_RECENT_BUYER_SLOTS} spots left
                    </div>
                </div>
            </div>

            <main className="relative overflow-hidden pb-24">
                <section className="mx-auto max-w-6xl px-4 pb-10 sm:pt-8 md:pb-14">
                    <div className="mt-8 gap-8">
                        <div>
                            <div className="inline-flex items-center gap-2 rounded-full border border-[#f6b31a]/30 bg-[#f6b31a]/10 px-4 py-2 text-[8px] md:text-[11px] font-semibold uppercase tracking-[0.26em] text-[#ffd26a]">
                                <Sparkles className="h-4 w-4" />
                                Watch this before you do anything else today
                            </div>

                            <h1 className="mt-5 max-w-4xl text-3xl font-black leading-[1.02] tracking-tight text-white sm:text-5xl md:text-6xl">
                                How to launch your online business today with{" "}
                                <span className="text-[#f6b31a] text-4xl sm:text-5xl md:text-6xl">₦200,000</span> and start
                                making sales in the next{" "}
                                <span className="text-[#f6b31a] text-4xl sm:text-6xl">48 hours</span>
                            </h1>
                        </div>

                        {/* <div className="rounded-[2rem] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.07),rgba(255,255,255,0.02))] p-5 shadow-[0_32px_90px_-48px_rgba(245,158,11,0.7)] backdrop-blur sm:p-6">
                            <h2 className="mt-3 text-2xl font-black leading-tight text-white sm:text-3xl">
                                This offer closes once 100 serious people take action.
                            </h2>

                            <div className="mt-6">
                                <div className="rounded-[1.4rem] border border-white/10 bg-black/25 p-4">
                                    <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-white/48">
                                        Spots left
                                    </p>
                                    <p className="mt-2 text-3xl font-black text-white">
                                        {recentBuyerSlotsLeft}
                                    </p>
                                    <p className="mt-2 text-sm text-white/62">
                                        out of {TOTAL_RECENT_BUYER_SLOTS} allocated spots
                                    </p>
                                </div>
                                <div className="rounded-[1.4rem] border border-white/10 bg-black/25 p-4 w-full">
                                    <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-white/48">
                                        Today&apos;s price
                                    </p>
                                    <p className="mt-2 text-3xl font-black text-[#f6b31a]">
                                        ₦{jbaConfig.amount.toLocaleString()}
                                    </p>
                                    <p className="mt-2 text-sm text-white/62">
                                        with onboarding and community access
                                    </p>
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={isFormUnlocked ? scrollToForm : scrollToVideo}
                                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#f59e0b] via-[#f6b31a] to-[#f97316] px-5 py-4 text-sm font-black uppercase tracking-[0.16em] text-[#13080e] transition hover:brightness-110"
                            >
                                {isFormUnlocked
                                    ? "Complete your checkout now"
                                    : "Watch the video above"}
                                <ArrowRight className="h-4 w-4" />
                            </button>
                        </div> */}
                    </div>
                </section>

                <section
                    ref={videoSectionRef}
                    className="mx-auto max-w-6xl scroll-mt-24 px-4 md:scroll-mt-32"
                >
                    <div className="rounded-[2rem] border border-white/10 bg-white/[0.04] p-3 shadow-[0_36px_100px_-52px_rgba(124,58,237,0.6)] backdrop-blur sm:p-5">
                        <div className="mb-4 h-2 overflow-hidden rounded-full bg-white/10">
                            <div
                                className={`h-full rounded-full transition-all duration-500 ease-out ${isFormUnlocked
                                    ? "bg-gradient-to-r from-emerald-400 via-emerald-300 to-lime-300"
                                    : "bg-gradient-to-r from-[#8b5cf6] via-[#a855f7] to-[#f6b31a]"
                                    }`}
                                style={{ width: `${displayedWatchPercentage}%` }}
                            />
                        </div>

                        <div className="overflow-hidden rounded-[1.6rem] border border-[#f59e0b]/35 bg-[#120819] shadow-[0_26px_90px_-36px_rgba(245,158,11,0.55)]">
                            <div
                                ref={videoContainerRef}
                                className="relative aspect-video w-full bg-black"
                            >
                                <video
                                    ref={videoRef}
                                    className="h-full w-full bg-black"
                                    preload="auto"
                                    controlsList="nodownload noremoteplayback noplaybackrate"
                                    controls={controlsEnabled}
                                    disablePictureInPicture
                                    muted={isMuted}
                                    playsInline
                                    autoPlay
                                    onContextMenu={(event) => event.preventDefault()}
                                    onClick={controlsEnabled ? undefined : togglePlayback}
                                >
                                    <source src={jbaConfig.videoUrl} type="video/mp4" />
                                    Your browser does not support the video tag.
                                </video>

                                {!isPlaying && (
                                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-gradient-to-b from-black/10 via-black/20 to-black/65">
                                        <button
                                            type="button"
                                            className="pointer-events-auto flex h-16 w-16 items-center justify-center rounded-full bg-white text-[#5520cc] shadow-[0_20px_50px_-20px_rgba(255,255,255,0.7)] transition hover:scale-105 md:h-20 md:w-20"
                                            aria-label="Play video"
                                            onClick={togglePlayback}
                                        >
                                            <Play className="ml-1 h-7 w-7 fill-current md:h-9 md:w-9" />
                                        </button>
                                    </div>
                                )}

                                <button
                                    type="button"
                                    className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-xl border border-white/10 bg-black/55 px-3 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-white shadow-lg backdrop-blur transition-colors hover:bg-black/75"
                                    aria-label="Restart video"
                                    onClick={restartVideo}
                                >
                                    <RotateCcw className="h-4 w-4" strokeWidth={2.1} />
                                    Restart
                                </button>

                                <button
                                    type="button"
                                    className="absolute bottom-4 right-4 flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-black/55 text-white shadow-lg backdrop-blur transition-colors hover:bg-black/75"
                                    aria-label="Toggle fullscreen"
                                    onClick={toggleFullscreen}
                                >
                                    <Maximize2 className="h-5 w-5" strokeWidth={2} />
                                </button>

                                {isMuted && (
                                    <button
                                        type="button"
                                        className="absolute bottom-4 left-4 rounded-xl border border-white/10 bg-black/60 px-3 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-white shadow-lg backdrop-blur transition-colors hover:bg-black/75"
                                        aria-label="Unmute video"
                                        onClick={handleUnmute}
                                    >
                                        Tap to unmute
                                    </button>
                                )}
                            </div>
                        </div>

                        {!isFormUnlocked  && <div className="mt-4 grid gap-3 sm:grid-cols-2">
                            <div className="rounded-[1.35rem] border border-white/10 bg-black/20 p-4">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-white/46">
                                    Community
                                </p>
                                <p className="mt-2 text-base font-semibold text-white">
                                    500+ Nigerian sellers building with Jellosite
                                </p>
                            </div>
                            <div className="rounded-[1.35rem] border border-white/10 bg-black/20 p-4">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-white/46">
                                    Access
                                </p>
                                <p className="mt-2 text-base font-semibold text-white">
                                    Setup begins in 24 to 48 hours after verified payment
                                </p>
                            </div>
                        </div>}
                    </div>
                </section>

                <section
                    ref={formRef}
                    className="relative mx-auto max-w-6xl scroll-mt-24 px-4 pb-6 pt-16 md:scroll-mt-32"
                >
                    <div className={`${isFormUnlocked ? 'grid gap-6 lg:grid-cols-[minmax(0,1.05fr)_minmax(280px,0.95fr)] lg:items-start' : ''}`}>
                        <div className="rounded-[2rem] border border-white/10 bg-white/[0.05] p-5 shadow-[0_32px_90px_-52px_rgba(124,58,237,0.6)] backdrop-blur sm:p-7">
                            {!isFormUnlocked ? (
                                <div className="text-center">
                                    <div className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-[#8b5cf6]/25 to-[#f6b31a]/25 text-[#ffd26a] sm:h-20 sm:w-20">
                                        <Lock className="h-8 w-8 sm:h-10 sm:w-10" strokeWidth={2} />
                                    </div>

                                    <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.3em] text-[#c4b5fd]">
                                        Instant access unlock
                                    </p>
                                    <h3 className="mt-3 text-2xl font-black text-white sm:text-3xl">
                                        Complete the video to unlock checkout.
                                    </h3>
                                    <p className="mx-auto mt-3 max-w-xl text-sm leading-7 text-white/68 sm:text-base">
                                        The payment form becomes available at minute 11. Finish
                                        the video and the page will scroll you here automatically.
                                    </p>

                                    <div className="mx-auto mt-8 max-w-md rounded-[1.6rem] border border-white/10 bg-black/20 p-5">
                                        <div className="flex items-center justify-between text-sm text-white/72">
                                            <span>Current progress</span>
                                            <span className="font-semibold text-white">
                                                {Math.round(displayedWatchPercentage)}%
                                            </span>
                                        </div>
                                        <div className="mt-4 h-2 overflow-hidden rounded-full bg-white/10">
                                            <div
                                                className="h-full rounded-full bg-gradient-to-r from-[#8b5cf6] via-[#a855f7] to-[#f6b31a]"
                                                style={{ width: `${displayedWatchPercentage}%` }}
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={scrollToVideo}
                                            className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-5 py-4 text-sm font-semibold uppercase tracking-[0.16em] text-white transition hover:bg-white/10"
                                        >
                                            Return to the video
                                            <ArrowRight className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    <div>
                                        <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#fda4af]">
                                            Today, you pay just ₦{jbaConfig.amount.toLocaleString()}
                                        </p>
                                        <h3 className="mt-3 text-2xl font-black text-white sm:text-3xl">
                                            Yes, I&apos;m ready to start today.
                                        </h3>
                                        <p className="mt-3 text-sm leading-7 text-white/68 sm:text-base">
                                            Enter your details below. We use them for payment
                                            verification, your receipt, and delivery of the
                                            materials after successful confirmation.
                                        </p>
                                    </div>

                                    <form className="space-y-4" onSubmit={handleProceed}>
                                        <div>
                                            <label
                                                htmlFor="jba-name"
                                                className="mb-2 block text-sm font-semibold text-white/78"
                                            >
                                                Full name
                                            </label>
                                            <input
                                                id="jba-name"
                                                type="text"
                                                value={leadForm.name}
                                                onChange={handleLeadFieldChange("name")}
                                                placeholder="Your full name"
                                                className={`h-12 w-full rounded-2xl border bg-black/25 px-4 text-white outline-none transition placeholder:text-white/28 focus:border-[#8b5cf6] ${formErrors.name
                                                    ? "border-red-400/80"
                                                    : "border-white/10"
                                                    }`}
                                            />
                                            {formErrors.name && (
                                                <p className="mt-2 text-sm text-red-300">{formErrors.name}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label
                                                htmlFor="jba-email"
                                                className="mb-2 block text-sm font-semibold text-white/78"
                                            >
                                                Email address
                                            </label>
                                            <input
                                                id="jba-email"
                                                type="email"
                                                value={leadForm.email}
                                                onChange={handleLeadFieldChange("email")}
                                                placeholder="name@example.com"
                                                className={`h-12 w-full rounded-2xl border bg-black/25 px-4 text-white outline-none transition placeholder:text-white/28 focus:border-[#8b5cf6] ${formErrors.email
                                                    ? "border-red-400/80"
                                                    : "border-white/10"
                                                    }`}
                                            />
                                            {formErrors.email && (
                                                <p className="mt-2 text-sm text-red-300">{formErrors.email}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label
                                                htmlFor="jba-phone"
                                                className="mb-2 block text-sm font-semibold text-white/78"
                                            >
                                                Phone number
                                            </label>
                                            <input
                                                id="jba-phone"
                                                type="tel"
                                                value={leadForm.phone}
                                                onChange={handleLeadFieldChange("phone")}
                                                placeholder="+234 801 234 5678"
                                                className={`h-12 w-full rounded-2xl border bg-black/25 px-4 text-white outline-none transition placeholder:text-white/28 focus:border-[#8b5cf6] ${formErrors.phone
                                                    ? "border-red-400/80"
                                                    : "border-white/10"
                                                    }`}
                                            />
                                            {formErrors.phone && (
                                                <p className="mt-2 text-sm text-red-300">{formErrors.phone}</p>
                                            )}
                                        </div>

                                        <button
                                            type="submit"
                                            disabled={isLaunchingPayment}
                                            className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#f59e0b] via-[#f6b31a] to-[#f97316] px-5 py-4 text-sm font-black uppercase tracking-[0.16em] text-[#12060f] transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-80"
                                        >
                                            {isLaunchingPayment
                                                ? "Preparing your payment..."
                                                : `Pay ₦${jbaConfig.amount.toLocaleString()}`}
                                            <ArrowRight className="h-4 w-4" />
                                        </button>

                                        {isLaunchingPayment && (
                                            <p className="rounded-2xl border border-[#f6b31a]/20 bg-[#f6b31a]/10 px-4 py-3 text-sm font-medium text-[#ffe29c]">
                                                Do not close or refresh this window while your
                                                payment is processing.
                                            </p>
                                        )}
                                    </form>
                                </div>
                            )}
                        </div>
                        {isFormUnlocked && (
                            <aside className="rounded-[2rem] border border-white/10 bg-[linear-gradient(180deg,rgba(139,92,246,0.12),rgba(255,255,255,0.03))] p-5 shadow-[0_32px_90px_-56px_rgba(139,92,246,0.72)] backdrop-blur sm:p-6">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#c4b5fd]">
                                    The complete package
                                </p>
                                <div className="mt-4 rounded-[1.5rem] border border-white/10 bg-black/25 p-5">
                                    <p className="text-sm text-white/60 line-through">
                                        Total value ₦590,000+
                                    </p>
                                    <p className="mt-2 text-4xl font-black text-white">
                                        ₦{jbaConfig.amount.toLocaleString()}
                                    </p>
                                    <p className="mt-2 text-sm leading-7 text-white/68">
                                        Instant access to the full package, founder guarantee,
                                        verified payment delivery, and onboarding support.
                                    </p>
                                </div>

                                <div className="mt-5 space-y-3">
                                    {PACKAGE_ITEMS.slice(0, 4).map((item) => (
                                        <div
                                            key={item.name}
                                            className="rounded-[1.4rem] border border-white/10 bg-black/20 p-4"
                                        >
                                            <div className="flex items-start gap-3">
                                                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#ffd26a]" />
                                                <div>
                                                    <p className="font-semibold text-white">
                                                        {item.name}
                                                    </p>
                                                    <p className="mt-1 text-sm leading-6 text-white/64">
                                                        {item.description}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
                                    <div className="rounded-[1.4rem] border border-white/10 bg-black/20 p-4">
                                        <div className="flex items-center gap-2 text-[#ffd26a]">
                                            <ShieldCheck className="h-5 w-5" />
                                            <span className="text-sm font-semibold text-white">
                                                Secure Paystack checkout
                                            </span>
                                        </div>
                                        <p className="mt-2 text-sm leading-6 text-white/62">
                                            Verified payment, delivery email, and receipt flow stay in
                                            place exactly as before.
                                        </p>
                                    </div>
                                    <div className="rounded-[1.4rem] border border-white/10 bg-black/20 p-4">
                                        <div className="flex items-center gap-2 text-[#ffd26a]">
                                            <Clock3 className="h-5 w-5" />
                                            <span className="text-sm font-semibold text-white">
                                                Setup begins quickly
                                            </span>
                                        </div>
                                        <p className="mt-2 text-sm leading-6 text-white/62">
                                            After successful verification, onboarding starts within 24
                                            to 48 hours.
                                        </p>
                                    </div>
                                </div>
                            </aside>
                        )}
                    </div>
                </section>

                <section className="mx-auto max-w-6xl px-4 py-8">
                    <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                        <div>
                            {/* <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#fda4af]">
                                Other gurus lied to you
                            </p> */}
                            <h2 className="mt-3 text-3xl font-black leading-tight text-white sm:text-5xl">
                                Let me remove the{" "}
                                <span className="text-[#f6b31a] text-3xl font-black leading-tight sm:text-5xl">brainwashing.</span>
                            </h2>
                        </div>
                    </div>

                    <div className="mt-8 grid gap-4 md:grid-cols-2">
                        {GURU_MYTHS.map((item) => (
                            <div
                                key={item.myth}
                                className="rounded-[1.7rem] border border-white/10 bg-white/[0.04] p-5 shadow-[0_24px_70px_-50px_rgba(245,158,11,0.75)]"
                            >
                                <div className="flex items-start gap-3">
                                    <div className="rounded-full bg-[#ef4444]/12 p-2 text-[#f87171]">
                                        <XCircle className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <p className="text-lg font-semibold leading-7 text-white line-through decoration-[#ef4444]/80">
                                            {item.myth}
                                        </p>
                                        <div className="mt-4 flex items-start gap-3">
                                            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#ffd26a]" />
                                            <p className="text-sm leading-7 text-white/66">
                                                {item.correction}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="mx-auto max-w-6xl px-4 py-16">
                    <div className="max-w-2xl">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#ffd26a]">
                            The 3 things
                        </p>
                        <h2 className="mt-3 text-3xl font-black leading-tight text-white sm:text-5xl">
                            Not ten. Not thirty.{" "}
                            <span className="text-[#f6b31a] text-3xl font-black leading-tight sm:text-5xl">Three.</span>
                        </h2>
                        <p className="mt-4 text-sm leading-7 text-white/64 sm:text-base">
                            These are the three things separating people actively taking
                            orders from the ones still writing business plans in their notes
                            app.
                        </p>
                    </div>

                    <div className="mt-8 grid gap-4 md:grid-cols-3">
                        {STARTING_STEPS.map((item) => (
                            <div
                                key={item.step}
                                className="rounded-[1.8rem] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.06),rgba(255,255,255,0.03))] p-5"
                            >
                                <div className="text-4xl font-black text-[#f6b31a]">
                                    {item.step}
                                </div>
                                <h3 className="mt-5 text-xl font-bold text-white">
                                    {item.title}
                                </h3>
                                <p className="mt-3 text-sm leading-7 text-white/64">
                                    {item.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="mx-auto max-w-5xl px-4 py-12 sm:py-16">
                    <div className="text-center">
                        <div className="inline-flex rounded-sm bg-[#8f1d1d] px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.32em] text-white shadow-[0_16px_28px_-20px_rgba(143,29,29,0.95)]">
                            The complete package
                        </div>
                        <h2 className="mx-auto mt-4 max-w-4xl text-3xl font-black leading-[1.02] tracking-tight text-white sm:text-5xl">
                            Everything you need to start selling by{" "}
                            <span className="text-[#f6b31a] text-3xl sm:text-5xl">this time tomorrow.</span>
                        </h2>
                        <p className="mx-auto mt-4 max-w-3xl text-sm leading-7 text-white/62 sm:text-base">
                            The complete package that has worked for over 1,000 Nigerian
                            businesses. The same system that takes you from watching this
                            video to running an actual business.
                        </p>
                    </div>

                    <div className="mt-8 rounded-[2rem] border border-[#f6b31a]/45 bg-[linear-gradient(180deg,rgba(255,255,255,0.03),rgba(255,255,255,0.01))] p-4 shadow-[0_36px_100px_-62px_rgba(246,179,26,0.9)] sm:p-6">
                        <div className="flex justify-center">
                            <div className="inline-flex rounded-sm border border-[#f6b31a]/50 bg-[#131218] px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-[#ffd26a]">
                                What you get today
                            </div>
                        </div>

                        <div className="mt-5 space-y-4">
                            {PACKAGE_ITEMS.map((item) => (
                                <div
                                    key={item.name}
                                    className="rounded-[1.45rem] border border-white/8 bg-black/18 px-4 py-4 sm:px-5"
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#f6b31a] text-[10px] font-black text-[#100b04]">
                                            +
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-base font-semibold leading-6 text-white sm:text-lg">
                                                {item.name}
                                            </p>
                                            <p className="mt-1 text-sm leading-6 text-white/58">
                                                {item.description}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-6 border-t border-[#f6b31a]/35 pt-5">
                            <div className="flex items-end justify-between gap-4">
                                <div className="text-[11px] font-semibold uppercase tracking-[0.26em] text-white/54">
                                    Total value
                                </div>
                                <div className="text-right">
                                    <p className="text-2xl font-black text-[#f6b31a] sm:text-3xl">
                                        ₦500,000+
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-6 rounded-[2rem] bg-[linear-gradient(180deg,#cf4d3f_0%,#b33631_100%)] px-4 py-6 text-center shadow-[0_40px_110px_-62px_rgba(207,77,63,0.95)] sm:px-6">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-white/70">
                            Today, for JBA only
                        </p>
                        <p className="mt-2 text-5xl font-black text-[#ffe08a] sm:text-6xl">
                            ₦{jbaConfig.amount.toLocaleString()}
                        </p>
                        <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-white/82">
                            That is about 88% off. This discount is for this page only and it
                            will not stay open forever.
                        </p>

                        <button
                            type="button"
                            onClick={isFormUnlocked ? scrollToForm : scrollToVideo}
                            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-[1.35rem] border border-[#f6b31a]/60 bg-gradient-to-r from-[#cf4d3f] via-[#d95b44] to-[#cc4134] px-5 py-4 text-sm font-black uppercase tracking-[0.16em] text-white shadow-[0_18px_34px_-18px_rgba(0,0,0,0.75),inset_0_0_0_1px_rgba(255,255,255,0.08)] transition hover:brightness-110 sm:mx-auto sm:max-w-md"
                        >
                            {isFormUnlocked
                                ? "Yes! I'm ready to start today"
                                : "Watch to unlock checkout"}
                            <ArrowRight className="h-4 w-4" />
                        </button>

                        <div className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[11px] font-medium text-white/65">
                            <span>Secure Paystack checkout</span>
                            <span>30-day founder guarantee</span>
                            <span>Onboarding call</span>
                        </div>

                        <div className="mx-auto mt-4 inline-flex items-center gap-3 rounded-full border border-[#f6b31a]/25 bg-[#111117]/70 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#ffe29c]">
                            <span className="rounded-full bg-[#7f1d1d] px-2 py-1 text-white">
                                {recentBuyerSlotsLeft} / {TOTAL_RECENT_BUYER_SLOTS}
                            </span>
                            <span>spots left. When they fill, the offer closes.</span>
                        </div>
                    </div>
                </section>

                <section className="mx-auto max-w-5xl px-4 py-14 sm:py-16">
                    <div className="text-center">
                        <div className="inline-flex rounded-sm bg-[#7c3aed] px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.32em] text-white shadow-[0_16px_28px_-20px_rgba(124,58,237,0.95)]">
                            Before you decide
                        </div>
                        <h2 className="mx-auto mt-4 max-w-3xl text-3xl font-black leading-[1.02] tracking-tight text-white sm:text-5xl">
                            Questions going through your head{" "}
                            <span className="text-[#f6b31a]">right now.</span>
                        </h2>
                    </div>

                    <div className="mx-auto mt-8 max-w-4xl space-y-4">
                        {FAQ_ITEMS.map((item) => (
                            <div
                                key={item.question}
                                className="rounded-[1.3rem] border border-[#f6b31a]/55 bg-[linear-gradient(180deg,rgba(255,255,255,0.03),rgba(255,255,255,0.015))] p-4 shadow-[0_24px_70px_-56px_rgba(246,179,26,0.95)] sm:p-5"
                            >
                                <div className="flex items-start gap-3">
                                    <div className="min-w-0 flex-1">
                                        <p className="text-base font-black leading-6 text-[#ffd26a] sm:text-lg">
                                            {item.question}
                                        </p>
                                        <p className="mt-3 text-sm leading-7 text-white/64">
                                            {item.answer}
                                        </p>
                                    </div>
                                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#f6b31a] text-[#0d0d10] shadow-[0_12px_24px_-16px_rgba(246,179,26,0.95)]">
                                        <ArrowRight className="h-4 w-4 -rotate-90" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="mx-auto max-w-5xl px-4 py-8 pb-16">
                    <div className="text-center">
                        <h2 className="mx-auto max-w-4xl text-4xl font-black leading-[0.98] tracking-tight text-white sm:text-6xl">
                            Six months from now,{" "}
                            <span className="text-[#f6b31a] max-w-4xl text-4xl sm:text-6xl leading-[0.98] tracking-tight">one of two things</span>{" "}
                            will be true.
                        </h2>
                        <p className="mx-auto mt-6 max-w-3xl text-sm leading-7 text-white/66 sm:text-base">
                            Either you&apos;ll have a business that&apos;s actually running,
                            actually taking orders, and actually making you money. Or
                            you&apos;ll still be planning.
                        </p>
                        <p className="mx-auto mt-5 max-w-3xl text-sm leading-7 text-white/66 sm:text-base">
                            The information keeping you stuck isn&apos;t more knowledge. You
                            already know enough. What&apos;s been missing is the structure, the
                            tools, and somebody showing you what to do first, second, and
                            third.
                        </p>
                    </div>

                    <div className="mx-auto mt-8 max-w-3xl rounded-[2rem] bg-[linear-gradient(180deg,#6f2a25_0%,#5f201d_100%)] px-4 py-6 shadow-[0_34px_100px_-60px_rgba(178,54,49,0.95)] sm:px-6">
                        <button
                            type="button"
                            onClick={isFormUnlocked ? scrollToForm : scrollToVideo}
                            className="inline-flex w-full items-center justify-center gap-2 rounded-[1.35rem] border border-[#f6b31a]/60 bg-gradient-to-r from-[#cf4d3f] via-[#d95b44] to-[#cc4134] px-5 py-4 text-sm font-black uppercase tracking-[0.16em] text-white shadow-[0_18px_34px_-18px_rgba(0,0,0,0.8),inset_0_0_0_1px_rgba(255,255,255,0.08)] transition hover:brightness-110"
                        >
                            {isFormUnlocked
                                ? "Give me instant access"
                                : "Keep watching to unlock"}
                            <ArrowRight className="h-4 w-4" />
                        </button>

                        <div className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[11px] font-medium text-white/65">
                            <span>Paystack secured</span>
                            <span>30-day guarantee</span>
                            <span>500+ students</span>
                        </div>
                    </div>

                    <div className="mx-auto mt-6 max-w-4xl space-y-4">
                        <div className="rounded-[1.65rem] border-l-4 border-[#f6b31a] bg-white/[0.03] px-5 py-5 shadow-[0_24px_70px_-56px_rgba(0,0,0,0.9)] sm:px-6">
                            <p className="text-xl font-black text-[#ffd26a]">P.S.</p>
                            <div className="mt-4 space-y-4 text-sm leading-7 text-white/68 sm:text-base">
                                <p>
                                    If you&apos;re still contemplating, I think you should just
                                    procrastinate and wait another six months. Seriously.
                                </p>
                                <p>
                                    But this offer is for serious-minded people who want to
                                    start their business as soon as possible. By this time
                                    tomorrow, you should be able to have your business fully set
                                    up and start taking orders immediately.
                                </p>
                                <p className="italic text-[#f6b31a]">
                                    So hop on this offer right now, click the button above, and
                                    I&apos;ll see you on the other side.
                                </p>
                                <p className="text-sm text-white/42">
                                    - Davies, CEO of Jellosite
                                </p>
                            </div>
                        </div>

                    </div>
                </section>
            </main>

            {isFormUnlocked && recentBuyerName && (
                <div className="pointer-events-none fixed bottom-3 left-3 right-3 z-40 sm:bottom-5 sm:left-5 sm:right-auto">
                    <div
                        aria-live="polite"
                        className={`w-full rounded-[1.35rem] border border-[#f6b31a]/20 bg-[#0d1016]/92 px-3.5 py-3 shadow-[0_24px_60px_-32px_rgba(0,0,0,0.9),0_18px_50px_-28px_rgba(245,158,11,0.65)] backdrop-blur-md transition-[transform,opacity] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] sm:w-[22rem] sm:px-4 ${isRecentBuyerVisible
                            ? "translate-x-0 opacity-100"
                            : "-translate-x-[115%] opacity-0"
                            }`}
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#ffd26a]">
                                    New action
                                </p>
                                <p className="mt-1 text-sm leading-6 text-white/80">
                                    <span className="font-semibold text-white">
                                        {recentBuyerName}
                                    </span>{" "}
                                    just took action.
                                </p>
                            </div>
                            <span className="shrink-0 whitespace-nowrap rounded-full border border-white/10 bg-white/6 px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.14em] text-white/70">
                                Just now
                            </span>
                        </div>

                        <div className="mt-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#f6b31a]">
                            <div className="h-2 w-2 rounded-full bg-[#f6b31a]" />
                            <span>
                                {recentBuyerSlotsLeft}{" "}
                                {recentBuyerSlotsLeft === 1 ? "slot" : "slots"} left
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {isFreePdfPromptOpen && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/75 p-4 backdrop-blur-md">
                    <div className="w-full max-w-md rounded-[2rem] border border-white/10 bg-[#0a0b11] p-6 shadow-[0_40px_120px_-60px_rgba(124,58,237,0.9)] md:p-7">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#c4b5fd]">
                                    Free bonus
                                </p>
                                <h3 className="mt-2 text-xl font-black leading-tight text-white">
                                    WAIT! BEFORE YOU CONTINUE WATCHING
                                </h3>
                            </div>
                            <button
                                type="button"
                                onClick={closeFreePdfPrompt}
                                disabled={freePdfCloseCountdown > 0 || isSubmittingFreePdf}
                                className="shrink-0 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-white/70 transition disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {isSubmittingFreePdf
                                    ? "Sending..."
                                    : freePdfCloseCountdown > 0
                                        ? `Close in ${freePdfCloseCountdown}s`
                                        : "Close"}
                            </button>
                        </div>

                        <p className="mt-4 text-sm leading-7 text-white/66">
                            Get your exact 30-day content plan Nigerian sellers are using to turn WhatsApp into paying customer. Free. Yours in 60 seconds.
                        </p>
                        <ul>
                            {FREE_PDF_BULLETS.map((bullet) => (
                                <li key={bullet} className="mt-3 flex items-start gap-3">
                                    <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-[#ffd26a]" />
                                    <p className="text-sm leading-7 text-white/66">{bullet}</p>
                                </li>
                            ))}
                        </ul>

                        <form className="mt-5 space-y-4" onSubmit={handleFreePdfSubmit}>
                            <div>
                                <label
                                    htmlFor="jba-free-pdf-name"
                                    className="mb-2 block text-sm font-semibold text-white/78"
                                >
                                    Full name
                                </label>
                                <input
                                    id="jba-free-pdf-name"
                                    type="text"
                                    value={freePdfLead.fullName}
                                    onChange={handleFreePdfFieldChange("fullName")}
                                    placeholder="Your full name"
                                    required
                                    className={`h-12 w-full rounded-2xl border bg-black/25 px-4 text-white outline-none transition placeholder:text-white/28 focus:border-[#8b5cf6] ${freePdfFormErrors.fullName
                                        ? "border-red-400/80"
                                        : "border-white/10"
                                        }`}
                                />
                                {freePdfFormErrors.fullName && (
                                    <p className="mt-2 text-sm text-red-300">
                                        {freePdfFormErrors.fullName}
                                    </p>
                                )}
                            </div>

                            <div>
                                <label
                                    htmlFor="jba-free-pdf-email"
                                    className="mb-2 block text-sm font-semibold text-white/78"
                                >
                                    Email
                                </label>
                                <input
                                    id="jba-free-pdf-email"
                                    type="email"
                                    value={freePdfLead.email}
                                    onChange={handleFreePdfFieldChange("email")}
                                    placeholder="name@example.com"
                                    className={`h-12 w-full rounded-2xl border bg-black/25 px-4 text-white outline-none transition placeholder:text-white/28 focus:border-[#8b5cf6] ${freePdfFormErrors.email
                                        ? "border-red-400/80"
                                        : "border-white/10"
                                        }`}
                                />
                                {freePdfFormErrors.email && (
                                    <p className="mt-2 text-sm text-red-300">
                                        {freePdfFormErrors.email}
                                    </p>
                                )}
                            </div>

                            <div>
                                <label
                                    htmlFor="jba-free-pdf-phone"
                                    className="mb-2 block text-sm font-semibold text-white/78"
                                >
                                    Phone number
                                </label>
                                <input
                                    id="jba-free-pdf-phone"
                                    type="tel"
                                    value={freePdfLead.phone}
                                    onChange={handleFreePdfFieldChange("phone")}
                                    placeholder="08012345678"
                                    className={`h-12 w-full rounded-2xl border bg-black/25 px-4 text-white outline-none transition placeholder:text-white/28 focus:border-[#8b5cf6] ${freePdfFormErrors.phone
                                        ? "border-red-400/80"
                                        : "border-white/10"
                                        }`}
                                />
                                {freePdfFormErrors.phone && (
                                    <p className="mt-2 text-sm text-red-300">
                                        {freePdfFormErrors.phone}
                                    </p>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={isSubmittingFreePdf}
                                className="inline-flex w-full items-center justify-center rounded-2xl bg-gradient-to-r from-[#8b5cf6] via-[#7c3aed] to-[#f59e0b] px-5 py-4 text-sm font-black uppercase tracking-[0.16em] text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-80"
                            >
                                {isSubmittingFreePdf ? "Sending your PDF..." : "Get the free PDF"}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default JBA;
